import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

// Services
import {
  AddMessageNotificationService,
  Notification,
} from '../../services/add-message/add-message-notification.service';
import { SelectedMessageDetailService } from '../../services/selected-message-detail/selected-message-detail.service';

// Models
import {
  StatementMessageModel,
} from '../../core/interfaces/statement-messaging-run.model';
import { AuthorizationService, UserPermissions } from '../../services/read-write-role/authorization.service';

@Component({
  selector: 'app-statement-messaging',
  templateUrl: './statement-messaging.component.html',
  styleUrl: './statement-messaging.component.scss',
  standalone: false,
})
export class StatementMessagingComponent implements OnInit, OnDestroy {
  // ============================================
  // Public Properties
  // ============================================
  isLoading = false;
  errorMessage = '';
  selectedMessageDetail: StatementMessageModel | null = null;
  currentNotification: Notification | null = null;
  // Make permissions available to template
  permissions: UserPermissions = {
    canView: false,
    canEdit: false,
    canCreate: false,
    canDelete: false,
    isReadOnly: true,
  };

  // ============================================
  // Services
  // ============================================
  private readonly notificationService = inject(AddMessageNotificationService);
  private readonly messageSelectionService = inject(SelectedMessageDetailService);
  protected readonly authService = inject(AuthorizationService);

  // ============================================
  // Private Properties
  // ============================================
  private readonly destroy$ = new Subject<void>();

  // ============================================
  // Lifecycle Hooks
  // ============================================

  ngOnInit(): void {
    this.subscribeToAuthorization();
    this.subscribeToMessageSelection();
    this.subscribeToMessageNotifications();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================
  // Subscriptions
  // ============================================

  /**
   * Subscribe to message selection changes
   */
  private subscribeToAuthorization(): void {
    this.authService.permissions$.subscribe((permissions) => {
      this.permissions = permissions;
    });
  }

  /**
   * Subscribe to message selection changes
   */
  private subscribeToMessageSelection(): void {
    this.messageSelectionService.selectedMessage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((message) => {
        this.selectedMessageDetail = message;
      });
  }

  /**
   * Subscribe to message notifications
   */
  private subscribeToMessageNotifications(): void {
    this.notificationService.notification$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notification) => {
        this.currentNotification = notification;
      });
  }

  // ============================================
  // Notification Actions
  // ============================================

  /**
   * Close message notification banner
   */
  closeNotification(): void {
    this.notificationService.clear();
    this.currentNotification = null;
  }
}
